import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Fab,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import { useAppDispatch, useAppSelector } from 'app';
import React, { useEffect } from 'react';
import GetPaymentSettings, { IGravityPaymentResponse, IGravitySettings } from './api/gravitypayment-api';
import AppSnackbar from 'components/snackbar/snackbar';
import { setTextToPayPaymentGateway } from 'components/authentication/components/authentication-slice';
import { useFormik } from 'formik';
import DealerAPI from 'components/authentication/api/dealer-api';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { SmallCircularProgress } from 'common/components/loaders';
import { TargetPayPaymentGateway } from 'common/utils/shared/permission-extensions';
import Alert from '@material-ui/lab/Alert';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

let initialValues: IGravitySettings = {
  paymentsData: [],
};
const GravityPaymentSettings: React.FC = () => {

  const dispatch = useAppDispatch();
  const dmsDealerIdCrm = useAppSelector((state) => state.user.dmsDealerId);
  const [showSnackBar] = AppSnackbar.useSnackBar();
  const [loadingLoc, setLoadingLoc] = React.useState(false);
  const [loadingSave, setLoadingSave] = React.useState(false);

  const alterFormikPaymentsData = (paymentValues: IGravityPaymentResponse[]) => {
    const values = paymentValues.map((paymentValue) => ({
      oid: paymentValue.oid === "0" ? "" : paymentValue.oid,
      authToken: paymentValue.authToken === "0" ? "" : paymentValue.authToken,
      secretPhrase: paymentValue.secretPhrase === "0" ? "" : paymentValue.secretPhrase,
      allowGiftCards: paymentValue.allowGiftCards,
      allowTextToPay: paymentValue.allowTextToPay,
      dealerId: paymentValue.dealerId,
      isSandbox: paymentValue.isSandbox,
      locationName: paymentValue.locationName,
      merchantKey: paymentValue.merchantKey,
      merchantName: paymentValue.merchantName,
    }));
    return values;
  }
  const isEmpty = (value?: string) => !value || value.trim() === '';

  const validate = (values: IGravitySettings) => {
    const errors: any = {};
    let allEmpty = true;
    values.paymentsData.forEach((data, idx) => {
      const paymentErrors: any = {};

      if (isEmpty(data.oid)) paymentErrors.oid = 'Oid is required';
      if (isEmpty(data.authToken)) paymentErrors.authToken = 'Auth Token is required';
      if (isEmpty(data.secretPhrase)) paymentErrors.secretPhrase = 'Secret Phrase is required';

      if (Object.keys(paymentErrors).length < 3) {
        allEmpty = false;
      }

      if (Object.keys(paymentErrors).length > 0 && Object.keys(paymentErrors).length < 3) {
        errors.paymentsData = errors.paymentsData || [];
        errors.paymentsData[idx] = paymentErrors;
      }
    });

    if (allEmpty) {
      errors.globalError = 'A payment setting must be configured for at least one location.';
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: initialValues,
    validate,
    onSubmit: (values) => {
      savePaymentSettings(values);
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    const getLocations = async () => {
      try {
        setLoadingLoc(true);
        const response = await DealerAPI.getDealerSettings(dmsDealerIdCrm);
        if (response.dealerLocationSettings) {
          const paymentResponse = response.dealerLocationSettings.map(async (x) => {
            const response = await GetPaymentSettings.fetchPaymentSettings(x.dealerId);
            if (response) {
              return { ...response, dealerId: x.dealerId, locationName: x.locationName };
            }
          });
          const paymentDataList = await Promise.all(paymentResponse);
          const formikValues = alterFormikPaymentsData(paymentDataList);
          initialValues.paymentsData = formikValues;
        }
        setLoadingLoc(false);
      } catch (error) {
        setLoadingLoc(false);
        return error;
      }
    };
    getLocations();
  }, []);

  async function savePaymentSettings(PaymentSettings: IGravitySettings) {
    try {
      setLoadingSave(true)
      let paymentGateway: number = 0;

      if (PaymentSettings.paymentsData.length === 0) {
        showSnackBar({ type: 'error', message: 'Please add payment settings' });
        return;
      }

      const onBoardResponse = await GetPaymentSettings.OnBoardGravityDealer();
      if (onBoardResponse !== "Success") {
        showSnackBar({
          type: 'error',
          message: 'Onboarding failed. Please contact the Target Pay team for assistance.',
        });
        return;
      }

      let hasError = false;
      for (const settings of PaymentSettings.paymentsData) {
        try {
          normalizePaymentFields(settings);
          const response = await GetPaymentSettings.updatePaymentSettings(settings);

          if (response === "") {
            if (isValidPaymentSettings(settings)) {
              paymentGateway = TargetPayPaymentGateway.Gravity;
            }
          } else {
            hasError = true;
          }
        } catch (error) {
          hasError = true;
        }
      }

      if (hasError) {
        showSnackBar({ type: 'error', message: 'Some payment settings could not be updated.' });
      } else {
        showSnackBar({ type: 'success', message: 'Payment settings saved successfully!' });
      }

      dispatch(setTextToPayPaymentGateway(paymentGateway));

      const formikValues = alterFormikPaymentsData(PaymentSettings.paymentsData)
      formik.setFieldValue('paymentsData', formikValues);

    } catch (e: any) {
      showSnackBar({ type: 'error', message: 'Unable to update payment settings' });
    }
    finally {
      setLoadingSave(false);
    }
  }

  const isInvalid = (value: string | null | undefined): boolean =>
    !value || value.trim() === "" || value.trim() === "0";

  function normalizePaymentFields(settings: IGravityPaymentResponse): void {
    if (isInvalid(settings.oid)) settings.oid = "0";
    if (isInvalid(settings.authToken)) settings.authToken = "0";
    if (isInvalid(settings.secretPhrase)) settings.secretPhrase = "0";
  }

  function isValidPaymentSettings(p: IGravityPaymentResponse): boolean {
    return !isInvalid(p.oid) && !isInvalid(p.authToken) && !isInvalid(p.secretPhrase);
  }

  return (
    <Paper style={{ paddingBottom: '30px' }}>
      <Grid container spacing={2}>
        <Grid item xs={3} />
        <Grid container xs={6} spacing={2} style={{ minHeight: '72vh', alignContent: 'flex-start' }}>
          <Grid item xs={12} style={{ paddingTop: '30px', textAlign: 'center' }}>
            <Typography variant="h4">EmergePay Settings</Typography>
          </Grid>
          <Box my={1} width={'100%'}>
            {loadingLoc ? (
              <Box style={{ margin: 'auto' }} display="flex" justifyContent="center">
                <SmallCircularProgress />
              </Box>
            ) : (
              <>
                {(formik.errors as any).globalError && (
                  <Alert severity="error">{(formik.errors as any).globalError}</Alert>
                )}
                {formik.values.paymentsData?.map((locations: IGravityPaymentResponse, idx: number) => {

                    const errorObj = formik.errors.paymentsData?.[idx];
                    const getFieldError = (field: keyof IGravityPaymentResponse) =>
                      typeof errorObj === 'object' && errorObj !== null ? errorObj[field] : undefined;

                    const oidError = getFieldError('oid');
                    const authTokenError = getFieldError('authToken');
                    const secretPhraseError = getFieldError('secretPhrase');
                    const hasAnyError = Boolean(oidError || authTokenError || secretPhraseError);

                    return (
                      <Accordion
                        key={locations.dealerId}
                        style={{
                          marginBottom: '10px',
                        }}
                        elevation={3}
                        defaultExpanded={idx === 0 || hasAnyError}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {locations.locationName}
                            {hasAnyError && <ErrorOutlineIcon color="error" fontSize="small" />}
                          </span>
                        </AccordionSummary>
                        <AccordionDetails>
                          <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
                            <Grid item xs={12} >
                              <TextField
                                fullWidth
                                variant="outlined"
                                margin="dense"
                                id={`paymentsData[${idx}].oid`}
                                name={`paymentsData[${idx}].oid`}
                                label="Oid"
                                placeholder="Oid"
                                onChange={(e) => {
                                  formik.setFieldValue(`paymentsData[${idx}].oid`, e.target.value);
                                }}
                                value={formik.values.paymentsData[idx].oid}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                error={Boolean(oidError)}
                                helperText={oidError}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                margin="dense"
                                id={`paymentsData[${idx}].authToken`}
                                name={`paymentsData[${idx}].authToken`}
                                label="Auth Token"
                                placeholder="Auth Token"
                                value={formik.values.paymentsData[idx].authToken}
                                onChange={(e) => {
                                  formik.setFieldValue(`paymentsData[${idx}].authToken`, e.target.value);
                                }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                error={Boolean(authTokenError)}
                                helperText={authTokenError}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                margin="dense"
                                id={`paymentsData[${idx}].secretPhrase`}
                                name={`paymentsData[${idx}].secretPhrase`}
                                label="Secret Phrase"
                                placeholder="Secret Phrase"
                                value={formik.values.paymentsData[idx].secretPhrase}
                                onChange={(e) => {
                                  formik.setFieldValue(`paymentsData[${idx}].secretPhrase`, e.target.value);
                                }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                error={Boolean(secretPhraseError)}
                                helperText={secretPhraseError}
                              />
                            </Grid>
                          </form>
                        </AccordionDetails>
                      </Accordion>
                    );
                  }
                  )}
              </>
            )}
          </Box>
        </Grid>
        <Grid container xs={3}>
          <Grid item xs={12} style={{ marginTop: '65px', marginLeft: '15px' }}>
            {!loadingLoc && (
              <Fab onClick={() => formik.handleSubmit()} title="Save Settings" color="primary" disabled={!formik.isValid || loadingSave}>
                {!loadingSave ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <SaveIcon />
                    <p style={{ fontSize: '7px', margin: '0px' }}>Save</p>
                  </div>
                ) : (
                  <Box style={{ margin: 'auto' }} display="flex" justifyContent="center">
                       <SmallCircularProgress />
                  </Box>
                )}

              </Fab>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};
export default GravityPaymentSettings;
